import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faStar,
  faChevronLeft,
  faChevronRight,
  faChevronDown,
  faChevronUp,
  faSearch,
  faSpinner,
  faArrowLeft,
  faTimes,
  faSync,
  faCheckCircle,
  faExclamationTriangle,
  faPhone,
  faUtensils,
  faRestroom,
  faInfoCircle,
  faCommentDots,
  faFilter,
  faPlus
} from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp, faGoogle } from '@fortawesome/free-brands-svg-icons';
import { formatIST } from '../utils/dateUtils';
import CustomerHistoryModal from './CustomerHistoryModal';
import './ReviewsPage.css';

const ReviewsPage = ({ token, business_id }) => {
  const navigate = useNavigate();

  // Handle Token and Business ID from Props or LocalStorage
  const activeToken = token || localStorage.getItem('token');
  const activeBusinessId = business_id || parseInt(localStorage.getItem('businessId'), 10);

  // Parse query params helper
  const getQueryParam = (name) => {
    const params = new URLSearchParams(window.location.search);
    return params.get(name);
  };

  const initialSource = (getQueryParam('source') || 'ALL').toUpperCase();
  const initialStatus = (getQueryParam('status') || getQueryParam('ticket_status') || 'ALL').toUpperCase();
  const initialRating = getQueryParam('rating') || 'ALL';
  const initialSearch = getQueryParam('search') || '';
  const initialStartDate = getQueryParam('start_date') || '';
  const initialEndDate = getQueryParam('end_date') || '';
  const initialDateRange = (initialStartDate || initialEndDate) ? 'CUSTOM' : 'ALL';

  // Target Review ID from query params to auto-open
  const initialReviewId = getQueryParam('review_id');
  let cleanInitialReviewId = null;
  if (initialReviewId) {
    let cleanId = initialReviewId;
    if (cleanId.toLowerCase().startsWith('rev')) {
      cleanId = cleanId.substring(3);
    }
    const parsedId = parseInt(cleanId, 10);
    if (!isNaN(parsedId)) {
      cleanInitialReviewId = parsedId;
    }
  }

  // Reviews and Meta State
  const [reviews, setReviews] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total_records: 0, total_pages: 1 });
  const [stats, setStats] = useState({ total_reviews: 0, open_tickets: 0, closed_tickets: 0, average_rating: 0.0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters State
  const [searchQuery, setSearchQuery] = useState(initialSearch);
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(initialSearch);
  const [selectedSource, setSelectedSource] = useState(initialSource);
  const [selectedStatus, setSelectedStatus] = useState(initialStatus);
  const [selectedRating, setSelectedRating] = useState(initialRating);
  const [dateRangeOption, setDateRangeOption] = useState(initialDateRange);
  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);
  const [sortBy, setSortBy] = useState('created_at_desc');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);

  const [targetReviewId, setTargetReviewId] = useState(cleanInitialReviewId);

  // Resolution Form State
  const [resolvingReview, setResolvingReview] = useState(null);
  const [managerExplanation, setManagerExplanation] = useState('');
  const [issueCategory, setIssueCategory] = useState('');
  const [publicReplyText, setPublicReplyText] = useState('');
  const [replyToUserWhatsapp, setReplyToUserWhatsapp] = useState(false);
  const [submittingResolve, setSubmittingResolve] = useState(false);
  const [resolveError, setResolveError] = useState(null);

  // Additive Filters Popover Toggle
  const [showAddFilterMenu, setShowAddFilterMenu] = useState(false);

  // Collapsible replies toggle state
  const [expandedReplies, setExpandedReplies] = useState({});

  const toggleReplyExpand = (reviewId) => {
    setExpandedReplies(prev => ({
      ...prev,
      [reviewId]: !prev[reviewId]
    }));
  };

  // Customer History Modal State
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);

  // Collapsible order details state
  const [expandedOrderDetails, setExpandedOrderDetails] = useState({});

  const handleOpenHistoryByName = (customerId) => {
    if (!customerId) {
      showToast('No customer history available for this review.', 'error');
      return;
    }
    setSelectedCustomerId(customerId);
    setShowHistoryModal(true);
  };

  const toggleOrderDetailsExpand = (reviewId) => {
    setExpandedOrderDetails(prev => ({
      ...prev,
      [reviewId]: !prev[reviewId]
    }));
  };

  const [isWinbackFilter, setIsWinbackFilter] = useState(false);

  // Global Actionable Toast Notifications
  const [toast, setToast] = useState(null);

  // 1. Handle Debouncing for Search Query (350ms)
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      setPage(1); // Reset to first page when search changes
    }, 350);

    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]);

  // 2. Adjust Date Filters based on DateRangeOption
  useEffect(() => {
    const today = new Date();
    const formatDateObj = (d) => d.toISOString().split('T')[0];

    if (dateRangeOption === 'TODAY') {
      setStartDate(formatDateObj(today));
      setEndDate(formatDateObj(today));
      setPage(1);
    } else if (dateRangeOption === 'YESTERDAY') {
      const yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);
      setStartDate(formatDateObj(yesterday));
      setEndDate(formatDateObj(yesterday));
      setPage(1);
    } else if (dateRangeOption === 'LAST_7_DAYS') {
      const sevenDaysAgo = new Date(today);
      sevenDaysAgo.setDate(today.getDate() - 7);
      setStartDate(formatDateObj(sevenDaysAgo));
      setEndDate(formatDateObj(today));
      setPage(1);
    } else if (dateRangeOption === 'LAST_30_DAYS') {
      const thirtyDaysAgo = new Date(today);
      thirtyDaysAgo.setDate(today.getDate() - 30);
      setStartDate(formatDateObj(thirtyDaysAgo));
      setEndDate(formatDateObj(today));
      setPage(1);
    } else if (dateRangeOption === 'ALL') {
      setStartDate('');
      setEndDate('');
      setPage(1);
    }
  }, [dateRangeOption]);

  // Helper to show toasts
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    // auto dismiss after 6 seconds
    setTimeout(() => {
      setToast(prev => (prev && prev.message === message ? null : prev));
    }, 6000);
  };

  // Fetch Reviews from Backend API
  const fetchReviews = async () => {
    if (!activeBusinessId) return;
    setLoading(true);
    setError(null);
    try {
      const apiBase = process.env.REACT_APP_API_URL || 'https://api.staging.waitwhiz.in';
      const url = new URL(`${apiBase}/api/v1/reviews/`);

      url.searchParams.append('business_id', activeBusinessId.toString());
      url.searchParams.append('page', page.toString());
      url.searchParams.append('limit', limit.toString());

      if (debouncedSearchQuery.trim()) {
        url.searchParams.append('search', debouncedSearchQuery.trim());
      }
      if (selectedSource && selectedSource !== 'ALL') {
        url.searchParams.append('source', selectedSource);
      }
      if (selectedStatus && selectedStatus !== 'ALL') {
        url.searchParams.append('ticket_status', selectedStatus);
      }
      if (selectedRating && selectedRating !== 'ALL') {
        url.searchParams.append('rating', selectedRating);
      }
      if (startDate) {
        url.searchParams.append('start_date', startDate);
      }
      if (endDate) {
        url.searchParams.append('end_date', endDate);
      }
      if (sortBy) {
        url.searchParams.append('sort_by', sortBy);
      }
      if (isWinbackFilter) {
        url.searchParams.append('is_winback', 'true');
      }

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${activeToken}`
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch reviews');
      }

      const result = await response.json();
      // WrapResponseMiddleware wraps successful response data inside result.data
      if (result.success && result.data) {
        let reviewsList = [];
        let totalRecords = 0;
        let totalPages = 1;
        let summaryStats = { total_reviews: 0, open_tickets: 0, closed_tickets: 0, average_rating: 0.0 };

        if (Array.isArray(result.data.reviews)) {
          // New structure
          reviewsList = result.data.reviews;
          totalRecords = result.data.pagination?.total_records || 0;
          totalPages = result.data.pagination?.total_pages || 1;
          summaryStats = result.data.stats || summaryStats;
        } else if (result.data.success && Array.isArray(result.data.data)) {
          // Old structure
          reviewsList = result.data.data;
          totalRecords = result.data.total || reviewsList.length;
          totalPages = Math.ceil(totalRecords / limit) || 1;

          let open = 0;
          let closed = 0;
          let sumRating = 0;
          let countRating = 0;

          reviewsList.forEach(r => {
            if (r.ticket_status === 'OPEN') open++;
            if (r.ticket_status === 'CLOSED') closed++;

            const rVal = parseRatingNumber(r.rating);
            if (rVal > 0) {
              sumRating += rVal;
              countRating++;
            }
          });

          summaryStats = {
            total_reviews: totalRecords,
            open_tickets: open,
            closed_tickets: closed,
            average_rating: countRating > 0 ? parseFloat((sumRating / countRating).toFixed(1)) : 0.0
          };
        }

        setReviews(reviewsList);
        setPagination({
          page: page,
          limit: limit,
          total_records: totalRecords,
          total_pages: totalPages
        });
        setStats(summaryStats);

        // Auto-open target review if specified in URL query params
        if (targetReviewId) {
          const matched = reviewsList.find(r => r.id === targetReviewId);
          if (matched) {
            setResolvingReview(matched);
            setTargetReviewId(null);
          } else {
            // Fallback: Fetch directly from our new backend GET endpoint
            const fetchSingleReview = async () => {
              try {
                const singleUrl = `${apiBase}/api/v1/reviews/${targetReviewId}`;
                const singleResponse = await fetch(singleUrl, {
                  method: 'GET',
                  headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${activeToken}`
                  }
                });
                if (singleResponse.ok) {
                  const singleResult = await singleResponse.json();
                  if (singleResult) {
                    setResolvingReview(singleResult);
                  }
                }
              } catch (err) {
                console.error('Error fetching single review by ID:', err);
              } finally {
                setTargetReviewId(null);
              }
            };
            fetchSingleReview();
          }
        }
      } else {
        throw new Error(result.message || 'API returned error');
      }
    } catch (err) {
      console.error('Fetch reviews error:', err);
      setError(err.message || 'An error occurred while fetching business reviews.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBusinessId, page, limit, debouncedSearchQuery, selectedSource, selectedStatus, selectedRating, startDate, endDate, sortBy, isWinbackFilter]);

  // Synchronize state filters and opened review ID to URL query parameters
  useEffect(() => {
    // Only run synchronization after the initial loading is complete to prevent overwriting values
    if (!loading) {
      const params = new URLSearchParams();

      if (resolvingReview) {
        params.set('review_id', `REV${resolvingReview.id}`);
      }
      if (selectedSource && selectedSource !== 'ALL') {
        params.set('source', selectedSource.toLowerCase());
      }
      if (selectedStatus && selectedStatus !== 'ALL') {
        params.set('status', selectedStatus.toLowerCase());
      }
      if (selectedRating && selectedRating !== 'ALL') {
        params.set('rating', selectedRating);
      }
      if (debouncedSearchQuery.trim()) {
        params.set('search', debouncedSearchQuery.trim());
      }
      if (startDate) {
        params.set('start_date', startDate);
      }
      if (endDate) {
        params.set('end_date', endDate);
      }

      const newSearch = params.toString();
      const currentSearch = window.location.search.replace(/^\?/, '');
      if (newSearch !== currentSearch) {
        navigate({
          pathname: window.location.pathname,
          search: newSearch ? `?${newSearch}` : ''
        }, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSource, selectedStatus, selectedRating, debouncedSearchQuery, startDate, endDate, resolvingReview, loading, navigate]);

  // Submit Resolution Patch to Backend API
  const handleResolveSubmit = async (e) => {
    e.preventDefault();
    if (!resolvingReview) return;
    if (!managerExplanation.trim()) {
      setResolveError('Manager explanation is required.');
      return;
    }

    setSubmittingResolve(true);
    setResolveError(null);

    try {
      const apiBase = process.env.REACT_APP_API_URL || 'https://api.staging.waitwhiz.in';
      const url = `${apiBase}/api/v1/reviews/${resolvingReview.id}/resolve`;

      const payload = {
        manager_explanation: managerExplanation.trim(),
        issue_category: issueCategory || null,
        public_reply_text: publicReplyText.trim() || null
      };

      if (replyToUserWhatsapp) {
        payload.reply_to_user_whatsapp = true;
      }

      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${activeToken}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorRes = await response.json().catch(() => ({}));
        throw new Error(errorRes.message || 'Failed to submit resolution. Please verify network connection.');
      }

      const result = await response.json();

      if (result.success && result.data) {
        const updatedReview = result.data;

        // Optimistic UI update: update review in local list immediately
        setReviews(prevReviews =>
          prevReviews.map(r => r.id === updatedReview.id ? updatedReview : r)
        );

        // Optimistic counter updates
        setStats(prevStats => {
          const wasOpen = resolvingReview.ticket_status === 'OPEN';
          const isOpen = updatedReview.ticket_status === 'OPEN';
          const wasClosed = resolvingReview.ticket_status === 'CLOSED';
          const isClosed = updatedReview.ticket_status === 'CLOSED';

          let deltaOpen = 0;
          let deltaClosed = 0;

          if (wasOpen && !isOpen) deltaOpen = -1;
          if (!wasOpen && isOpen) deltaOpen = 1;
          if (wasClosed && !isClosed) deltaClosed = -1;
          if (!wasClosed && isClosed) deltaClosed = 1;

          return {
            ...prevStats,
            open_tickets: Math.max(0, prevStats.open_tickets + deltaOpen),
            closed_tickets: Math.max(0, prevStats.closed_tickets + deltaClosed)
          };
        });

        // External API sync state / Partial sync failure warning check
        if (updatedReview.public_reply_status === 'FAILED') {
          showToast(`Resolved internally, but public reply sync failed: ${updatedReview.public_reply_error || 'Expired credentials or third-party API limit.'}`, 'warning');
        } else if (updatedReview.public_reply_status === 'PENDING') {
          showToast('Ticket closed! Public reply sync is pending.', 'success');
        } else {
          showToast('Ticket resolved and closed successfully!', 'success');
        }

        // Close resolution modal and reset inputs
        setResolvingReview(null);
        setManagerExplanation('');
        setIssueCategory('');
        setPublicReplyText('');
        setReplyToUserWhatsapp(false);
      } else {
        throw new Error(result.message || 'API returned success status but response data was missing.');
      }
    } catch (err) {
      console.error('Resolve ticket error:', err);
      setResolveError(err.message || 'Resolution submission failed.');
    } finally {
      setSubmittingResolve(false);
    }
  };

  // Helper formats
  const formatDate = (dateStr) => formatIST(dateStr, 'short');

  const getInitials = (name) => {
    if (!name) return 'AN';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  const getAvatarColor = (name) => {
    if (!name) return '#64748b'; // slate grey
    const colors = [
      '#6366f1', // Indigo
      '#0284c7', // Sky Blue
      '#ec4899', // Pink
      '#059669', // Emerald Green
      '#d97706', // Amber Yellow
      '#7c3aed', // Violet
      '#dc2626', // Red
      '#0891b2', // Cyan
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % colors.length;
    return colors[index];
  };

  const parseRatingNumber = (r) => {
    if (!r) return 0;
    const clean = r.toString().toLowerCase().trim();
    if (clean.startsWith('5')) return 5;
    if (clean.startsWith('4')) return 4;
    if (clean.startsWith('3')) return 3;
    if (clean.startsWith('2')) return 2;
    if (clean.startsWith('1')) return 1;
    return 0;
  };

  const renderStars = (ratingVal) => {
    const starsNum = parseRatingNumber(ratingVal);
    const starArray = [];
    for (let i = 1; i <= 5; i++) {
      starArray.push(
        <FontAwesomeIcon
          key={i}
          icon={faStar}
          className={i <= starsNum ? 'rp-star-filled' : 'rp-star-empty'}
        />
      );
    }
    return starArray;
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedSource('ALL');
    setSelectedStatus('ALL');
    setSelectedRating('ALL');
    setDateRangeOption('ALL');
    setStartDate('');
    setEndDate('');
    setSortBy('created_at_desc');
    setIsWinbackFilter(false);
    setPage(1);
    setShowAddFilterMenu(false);
  };

  const handleStatCardClick = (statusType) => {
    setSelectedStatus(statusType);
    setPage(1);
  };

  const activeFiltersCount =
    (selectedSource !== 'ALL' ? 1 : 0) +
    (selectedStatus !== 'ALL' ? 1 : 0) +
    (selectedRating !== 'ALL' ? 1 : 0) +
    (dateRangeOption !== 'ALL' ? 1 : 0) +
    (isWinbackFilter ? 1 : 0) +
    (searchQuery.trim() ? 1 : 0);

  const getNssColorClass = (score) => {
    if (score === undefined || score === null) return 'rp-stat-nss-bad';
    const num = typeof score === 'number' ? score : parseFloat(score) || 0;
    if (num < 30) return 'rp-stat-nss-bad';
    if (num <= 50) return 'rp-stat-nss-good';
    return 'rp-stat-nss-excellent';
  };

  return (
    <div className="rp-container">
      {/* Toast Alert popup */}
      {toast && (
        <div className={`rp-toast rp-toast-${toast.type}`}>
          <div className="rp-toast-icon">
            <FontAwesomeIcon icon={toast.type === 'success' ? faCheckCircle : faExclamationTriangle} />
          </div>
          <div className="rp-toast-message">{toast.message}</div>
          <button className="rp-toast-close" onClick={() => setToast(null)}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      )}

      {/* Header section */}
      <div className="rp-header">
        <button className="rp-back-btn" onClick={() => navigate('/')}>
          <FontAwesomeIcon icon={faArrowLeft} />
          <span>Dashboard</span>
        </button>
        <div className="rp-header-title-container">
          <h1 className="rp-title">Customer Feedback & Reviews</h1>
          <p className="rp-subtitle">Monitor and respond to customer reviews across all integrations</p>
        </div>
        <button className="rp-refresh-btn" onClick={fetchReviews} disabled={loading}>
          <FontAwesomeIcon icon={faSync} spin={loading} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Stats Summary Panel - 4 Main Cards */}
      <div className="rp-stats-grid">
        <div
          className={`rp-stat-card rp-stat-card-clickable ${selectedStatus === 'ALL' && !isWinbackFilter ? 'rp-stat-card-active' : ''}`}
          onClick={() => { setSelectedStatus('ALL'); setIsWinbackFilter(false); setPage(1); }}
          title="Click to show all reviews"
        >
          <span className="rp-stat-label">Total Reviews</span>
          <span className="rp-stat-value">{stats.total_reviews}</span>
        </div>

        <div
          className={`rp-stat-card rp-stat-card-open rp-stat-card-clickable ${selectedStatus === 'OPEN' && !isWinbackFilter ? 'rp-stat-card-active' : ''}`}
          onClick={() => { handleStatCardClick('OPEN'); setIsWinbackFilter(false); }}
          title="Click to filter Open Tickets"
        >
          <span className="rp-stat-label">Open Tickets</span>
          <span className="rp-stat-value">{stats.open_tickets}</span>
          {stats.open_tickets > 0 && <span className="rp-pulse-badge">Requires Action</span>}
        </div>

        <div
          className={`rp-stat-card rp-stat-card-closed rp-stat-card-clickable ${selectedStatus === 'CLOSED' && !isWinbackFilter ? 'rp-stat-card-active' : ''}`}
          onClick={() => { handleStatCardClick('CLOSED'); setIsWinbackFilter(false); }}
          title="Click to filter Resolved Tickets"
        >
          <span className="rp-stat-label">Resolved Tickets | Winback</span>
          <span className="rp-stat-value">{stats.closed_tickets} | {stats.winback_count || 0}</span>
        </div>

        <div className="rp-stat-card">
          <span className="rp-stat-label">Average Rating | NSS</span>
          <div className="rp-stat-rating-value">
            <span className="rp-stat-value">{stats.average_rating}</span>
            <FontAwesomeIcon icon={faStar} className="rp-star-filled rp-large-star" />
            <span
              className={`rp-stat-nss-pill ${getNssColorClass(stats.nss_score)}`}
              title="Net Sentiment Score: % of 5★ minus % of 3★ or lower (<30 Bad, 30-50 Good, >50 Excellent)"
            >
              {stats.nss_score !== undefined && stats.nss_score !== null
                ? `${stats.nss_score > 0 ? '+' : ''}${stats.nss_score}`
                : '0'}
            </span>
          </div>
        </div>
      </div>

      {/* Standalone Winback Overview Card Centered Below */}
      <div className="rp-winback-overview-wrapper">
        <div
          className={`rp-stat-card rp-stat-card-winback-full rp-stat-card-clickable ${isWinbackFilter ? 'rp-stat-card-active' : ''}`}
          onClick={() => { setIsWinbackFilter(!isWinbackFilter); setPage(1); }}
          title="Click to filter Winbacks"
        >
          <div className="rp-winback-full-header">
            <span className="rp-stat-label">Winback Overview</span>
            {isWinbackFilter && <span className="rp-winback-active-indicator">Filter Active</span>}
          </div>
          <div className="rp-winback-full-metrics">
            <div className="rp-winback-full-metric-item">
              <span className="rp-winback-full-val">{stats.winback_count || 0}</span>
              <span className="rp-winback-full-sub">Winbacks</span>
            </div>
            <div className="rp-winback-full-divider" />
            <div className="rp-winback-full-metric-item">
              <span className="rp-winback-full-val">{stats.winback_percentage || 0}%</span>
              <span className="rp-winback-full-sub">Winback Rate</span>
            </div>
            <div className="rp-winback-full-divider" />
            <div className="rp-winback-full-metric-item">
              <span className="rp-winback-full-val">₹{(stats.winback_revenue || 0).toLocaleString('en-IN')}</span>
              <span className="rp-winback-full-sub">Revenue Saved</span>
            </div>
          </div>
        </div>
      </div>

      {/* Filters & Search Control Bar */}
      <div className="rp-filters-panel">
        <div className="rp-controls-top-row">
          {/* Search Box */}
          <div className="rp-search-wrapper">
            <FontAwesomeIcon icon={faSearch} className="rp-search-icon" />
            <input
              type="text"
              className="rp-search-input"
              placeholder="Search by name or phone..."
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
            />
            {searchQuery && (
              <button className="rp-clear-search-btn" onClick={() => setSearchQuery('')}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            )}
          </div>

          {/* Add Filter & Quick Actions */}
          <div className="rp-actions-wrapper">
            {/* Add Filter Button & Popover */}
            <div className="rp-add-filter-container">
              <button
                type="button"
                className={`rp-add-filter-btn ${showAddFilterMenu ? 'active' : ''}`}
                onClick={() => setShowAddFilterMenu(!showAddFilterMenu)}
              >
                <FontAwesomeIcon icon={faFilter} />
                <span>Add Filter</span>
                <FontAwesomeIcon icon={faPlus} className="rp-plus-icon" />
              </button>

              {/* Add Filter Dropdown Popover */}
              {showAddFilterMenu && (
                <div className="rp-filter-popover">
                  <div className="rp-filter-popover-header">
                    <span>Select Filter</span>
                    <button type="button" onClick={() => setShowAddFilterMenu(false)}>
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </div>

                  <div className="rp-filter-popover-body">
                    {/* Source Filter Selector */}
                    <div className="rp-popover-item">
                      <label>Source</label>
                      <select
                        value={selectedSource}
                        onChange={(e) => {
                          setSelectedSource(e.target.value);
                          setPage(1);
                          setShowAddFilterMenu(false);
                        }}
                      >
                        <option value="ALL">All Sources</option>
                        <option value="DIRECT">WhatsApp</option>
                        <option value="QR_WASHROOM">Washrooms QR</option>
                        <option value="GOOGLE">Google Reviews</option>
                        <option value="ZOMATO">Zomato Reviews</option>
                      </select>
                    </div>

                    {/* Rating Filter Selector */}
                    <div className="rp-popover-item">
                      <label>Rating</label>
                      <select
                        value={selectedRating}
                        onChange={(e) => {
                          setSelectedRating(e.target.value);
                          setPage(1);
                          setShowAddFilterMenu(false);
                        }}
                      >
                        <option value="ALL">All Ratings</option>
                        <option value="5">5 Stars</option>
                        <option value="4">4 Stars</option>
                        <option value="3">3 Stars</option>
                        <option value="2">2 Stars</option>
                        <option value="1">1 Star</option>
                        <option value="3 star or less">3 Stars or less</option>
                      </select>
                    </div>

                    {/* Status Filter Selector */}
                    <div className="rp-popover-item">
                      <label>Status</label>
                      <select
                        value={selectedStatus}
                        onChange={(e) => {
                          setSelectedStatus(e.target.value);
                          setPage(1);
                          setShowAddFilterMenu(false);
                        }}
                      >
                        <option value="ALL">All Tickets</option>
                        <option value="OPEN">Open Tickets</option>
                        <option value="CLOSED">Closed/Resolved</option>
                        <option value="NOT_APPLICABLE">Not Applicable</option>
                      </select>
                    </div>

                    {/* Date Range Selector */}
                    <div className="rp-popover-item">
                      <label>Date Range</label>
                      <select
                        value={dateRangeOption}
                        onChange={(e) => {
                          setDateRangeOption(e.target.value);
                          setPage(1);
                          if (e.target.value !== 'CUSTOM') {
                            setShowAddFilterMenu(false);
                          }
                        }}
                      >
                        <option value="ALL">All Time</option>
                        <option value="TODAY">Today</option>
                        <option value="YESTERDAY">Yesterday</option>
                        <option value="LAST_7_DAYS">Last 7 Days</option>
                        <option value="LAST_30_DAYS">Last 30 Days</option>
                        <option value="CUSTOM">Custom Date...</option>
                      </select>
                    </div>

                    {dateRangeOption === 'CUSTOM' && (
                      <div className="rp-popover-custom-dates">
                        <div className="rp-popover-item">
                          <label>From</label>
                          <input
                            type="date"
                            value={startDate}
                            onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
                          />
                        </div>
                        <div className="rp-popover-item">
                          <label>To</label>
                          <input
                            type="date"
                            value={endDate}
                            onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
                          />
                        </div>
                      </div>
                    )}
                    {/* Winback Only Checkbox Selector */}
                    <div className="rp-popover-item">
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', margin: 0 }}>
                        <input
                          type="checkbox"
                          checked={isWinbackFilter}
                          onChange={(e) => {
                            setIsWinbackFilter(e.target.checked);
                            setPage(1);
                            setShowAddFilterMenu(false);
                          }}
                        />
                        <span>Winback Only</span>
                      </label>
                    </div>

                    {/* Items Per Page Limit Selector */}
                    <div className="rp-popover-item">
                      <label>Page Size</label>
                      <select value={limit} onChange={(e) => { setLimit(parseInt(e.target.value, 10)); setPage(1); }}>
                        <option value="10">10 per page</option>
                        <option value="20">20 per page</option>
                        <option value="50">50 per page</option>
                        <option value="100">100 per page</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Sort Dropdown */}
            <div className="rp-sort-wrapper">
              <select value={sortBy} onChange={(e) => { setSortBy(e.target.value); setPage(1); }}>
                <option value="created_at_desc">Newest First</option>
                <option value="created_at_asc">Oldest First</option>
                <option value="rating_desc">High Rating</option>
                <option value="rating_asc">Low Rating</option>
              </select>
            </div>
          </div>
        </div>

        {/* Active Filter Chips Bar */}
        {activeFiltersCount > 0 && (
          <div className="rp-active-chips-bar">
            <span className="rp-active-chips-label">Active Filters:</span>
            <div className="rp-chips-list">
              {isWinbackFilter && (
                <div className="rp-filter-chip">
                  <span>Filter: Winbacks Only</span>
                  <button type="button" onClick={() => setIsWinbackFilter(false)}>
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>
              )}
              {selectedStatus !== 'ALL' && (
                <div className="rp-filter-chip">
                  <span>Status: {
                    selectedStatus === 'OPEN' ? 'Open Tickets' :
                      selectedStatus === 'CLOSED' ? 'Closed/Resolved' :
                        'Not Applicable'
                  }</span>
                  <button type="button" onClick={() => setSelectedStatus('ALL')}>
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>
              )}

              {selectedSource !== 'ALL' && (
                <div className="rp-filter-chip">
                  <span>Source: {
                    selectedSource === 'DIRECT' ? 'WhatsApp' :
                      selectedSource === 'QR_WASHROOM' ? 'Washrooms QR' :
                        selectedSource === 'GOOGLE' ? 'Google Reviews' :
                          'Zomato Reviews'
                  }</span>
                  <button type="button" onClick={() => setSelectedSource('ALL')}>
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>
              )}

              {selectedRating !== 'ALL' && (
                <div className="rp-filter-chip">
                  <span>Rating: {
                    selectedRating === '3 star or less' ? '≤ 3 Stars' : `${selectedRating} Stars`
                  }</span>
                  <button type="button" onClick={() => setSelectedRating('ALL')}>
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>
              )}

              {dateRangeOption !== 'ALL' && (
                <div className="rp-filter-chip">
                  <span>Date: {
                    dateRangeOption === 'TODAY' ? 'Today' :
                      dateRangeOption === 'YESTERDAY' ? 'Yesterday' :
                        dateRangeOption === 'LAST_7_DAYS' ? 'Last 7 Days' :
                          dateRangeOption === 'LAST_30_DAYS' ? 'Last 30 Days' :
                            `Custom (${startDate || 'From'} - ${endDate || 'To'})`
                  }</span>
                  <button type="button" onClick={() => { setDateRangeOption('ALL'); setStartDate(''); setEndDate(''); }}>
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>
              )}

              {searchQuery.trim() && (
                <div className="rp-filter-chip">
                  <span>Search: "{searchQuery.trim()}"</span>
                  <button type="button" onClick={() => setSearchQuery('')}>
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>
              )}

              <button type="button" className="rp-clear-all-chips-btn" onClick={handleClearFilters}>
                Clear All
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Main Reviews Container */}
      <div className="rp-reviews-container">
        {loading && reviews.length === 0 ? (
          <div className="rp-empty-state">
            <FontAwesomeIcon icon={faSpinner} spin className="rp-empty-icon rp-loading-spinner" />
            <p>Loading reviews...</p>
          </div>
        ) : error ? (
          <div className="rp-empty-state rp-error-state">
            <FontAwesomeIcon icon={faExclamationTriangle} className="rp-empty-icon" />
            <h3>Error Loading Reviews</h3>
            <p>{error}</p>
            <button className="rp-retry-btn" onClick={fetchReviews}>Try Again</button>
          </div>
        ) : reviews.length === 0 ? (
          <div className="rp-empty-state">
            <FontAwesomeIcon icon={faInfoCircle} className="rp-empty-icon" />
            <h3>No Reviews Found</h3>
            <p>No feedback matching your selected filters could be found.</p>
            <button className="rp-clear-all-btn" onClick={handleClearFilters}>Clear Filters</button>
          </div>
        ) : (
          <div className="rp-reviews-list">
            {reviews.map((review) => {
              const starsVal = parseRatingNumber(review.rating);
              const isNegative = starsVal <= 3 && starsVal > 0;
              const hasSubRatings = review.food_rating || review.service_rating || review.ambience_rating;
              const isWashroom = review.source === 'QR_WASHROOM';
              const reviewerDisplayName = review.reviewer_name || 'Anonymous Guest';
              const targetCustomerId = review.customer_id || review.metadata?.customer_id || review.metadata?.reviewer?.customer_id || review.metadata?.order_summary?.customer_id;

              return (
                <div key={review.id} className={`rp-card ${isNegative && review.ticket_status === 'OPEN' ? 'rp-card-negative' : ''}`}>
                  {/* Card Header */}
                  <div className="rp-card-header">
                    <div className="rp-reviewer-info">
                      {review.metadata?.reviewer?.profilePhotoUrl || review.metadata?.profilePhotoUrl || review.metadata?.profile_photo_url ? (
                        <img
                          src={review.metadata?.reviewer?.profilePhotoUrl || review.metadata?.profilePhotoUrl || review.metadata?.profile_photo_url}
                          alt={reviewerDisplayName}
                          className={`rp-reviewer-avatar-img ${targetCustomerId ? 'rp-avatar-clickable' : ''}`}
                          onClick={() => handleOpenHistoryByName(targetCustomerId)}
                          title={targetCustomerId ? "Click to view customer history" : "No customer history available"}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      ) : (
                        <div
                          className={`rp-reviewer-avatar ${targetCustomerId ? 'rp-avatar-clickable' : ''}`}
                          style={{ backgroundColor: getAvatarColor(reviewerDisplayName) }}
                          onClick={() => handleOpenHistoryByName(targetCustomerId)}
                          title={targetCustomerId ? "Click to view customer history" : "No customer history available"}
                        >
                          {getInitials(reviewerDisplayName)}
                        </div>
                      )}
                      <div className="rp-reviewer-details">
                        <span
                          className="rp-reviewer-name rp-reviewer-name-clickable"
                          onClick={() => handleOpenHistoryByName(targetCustomerId)}
                          title={targetCustomerId ? "Click to view customer history" : "No customer history available"}
                        >
                          {reviewerDisplayName}
                        </span>
                        {review.reviewer_contact && (
                          <a href={`tel:${review.reviewer_contact}`} className="rp-reviewer-contact">
                            <FontAwesomeIcon icon={faPhone} />
                            <span>{review.reviewer_contact}</span>
                          </a>
                        )}
                      </div>
                    </div>

                    {/* Stars and Badges */}
                    <div className="rp-badges-container">
                      {/* Rating representation */}
                      {starsVal > 0 ? (
                        <div className="rp-rating-stars">
                          {renderStars(review.rating)}
                        </div>
                      ) : (
                        review.rating && <span className="rp-custom-rating">{review.rating}</span>
                      )}

                      {/* Source badge */}
                      <span className={`rp-source-badge rp-source-${review.source.toLowerCase()}`}>
                        {review.source === 'DIRECT' && (
                          <>
                            <FontAwesomeIcon icon={faWhatsapp} />
                            <span>WhatsApp</span>
                          </>
                        )}
                        {review.source === 'QR_WASHROOM' && (
                          <>
                            <FontAwesomeIcon icon={faRestroom} />
                            <span>Washroom</span>
                          </>
                        )}
                        {review.source === 'GOOGLE' && (
                          <>
                            <FontAwesomeIcon icon={faGoogle} />
                            <span>Google</span>
                          </>
                        )}
                        {review.source === 'ZOMATO' && (
                          <>
                            <FontAwesomeIcon icon={faUtensils} />
                            <span>Zomato</span>
                          </>
                        )}
                      </span>

                      {/* Ticket Status */}
                      <span className={`rp-status-badge rp-status-${review.ticket_status.toLowerCase()}`}>
                        {review.ticket_status === 'OPEN' && <span className="rp-pulse-dot" />}
                        <span>{review.ticket_status}</span>
                      </span>

                      {/* Winback Badge */}
                      {review.is_winback && (
                        <span className="rp-badge-winback" title={`Winback verified via ${review.winback_type || 'system'}${review.winback_date ? ' on ' + formatDate(review.winback_date) : ''}`}>
                          <FontAwesomeIcon icon={faCheckCircle} />
                          <span>Winback ({review.winback_type || 'YES'})</span>
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Sub ratings if present */}
                  {hasSubRatings && (
                    <div className="rp-subratings-row">
                      {review.food_rating && (
                        <div className="rp-subrating-pill">
                          <span className="rp-subrating-label">Food</span>
                          <span className="rp-subrating-score">{review.food_rating}★</span>
                        </div>
                      )}
                      {review.service_rating && (
                        <div className="rp-subrating-pill">
                          <span className="rp-subrating-label">Service</span>
                          <span className="rp-subrating-score">{review.service_rating}★</span>
                        </div>
                      )}
                      {review.ambience_rating && (
                        <div className="rp-subrating-pill">
                          <span className="rp-subrating-label">Ambience</span>
                          <span className="rp-subrating-score">{review.ambience_rating}★</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Review Text Body */}
                  {review.text && (
                    <div className="rp-card-body">
                      <p className="rp-review-text">"{review.text}"</p>
                    </div>
                  )}

                  {/* Washroom Metadata details */}
                  {isWashroom && review.metadata && (
                    <div className="rp-metadata-container">
                      {review.metadata.washroom_condition && (
                        <span className="rp-metadata-tag">
                          Condition: <strong>{review.metadata.washroom_condition}</strong>
                        </span>
                      )}
                      {Array.isArray(review.metadata.issues) && review.metadata.issues.length > 0 && (
                        <div className="rp-metadata-issues">
                          <span className="rp-metadata-issues-label">Reported:</span>
                          {review.metadata.issues.map((issue, idx) => (
                            <span key={idx} className="rp-issue-capsule">{issue}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Order Details Preview Box */}
                  {review.metadata?.order_summary && (
                    <div className="rp-order-summary-box">
                      <div className="rp-order-summary-header">
                        <span className="rp-order-summary-title">
                          <FontAwesomeIcon icon={faUtensils} className="rp-order-icon" />
                          Order Details ({review.metadata.order_summary.order_type || 'DINE_IN'})
                        </span>
                        {review.metadata.order_summary.total_amount > 0 && (
                          <span className="rp-order-total-badge">
                            ₹{review.metadata.order_summary.total_amount}
                          </span>
                        )}
                      </div>
                      <div className="rp-order-summary-meta">
                        {review.metadata.order_summary.table_number && (
                          <span className="rp-order-meta-item">
                            Table: <strong>{review.metadata.order_summary.table_number}</strong>
                          </span>
                        )}
                        {review.metadata.order_summary.no_of_guests && (
                          <span className="rp-order-meta-item">
                            Guests: <strong>{review.metadata.order_summary.no_of_guests}</strong>
                          </span>
                        )}
                      </div>
                      {Array.isArray(review.metadata.order_summary.item_names) && review.metadata.order_summary.item_names.length > 0 && (
                        <div className="rp-order-items-container">
                          <span className="rp-order-items-label">Items:</span>
                          <div className="rp-order-items-chips">
                            {(expandedOrderDetails[review.id]
                              ? review.metadata.order_summary.item_names
                              : review.metadata.order_summary.item_names.slice(0, 4)
                            ).map((itemName, i) => (
                              <span key={i} className="rp-order-item-chip">{itemName}</span>
                            ))}
                            {review.metadata.order_summary.item_names.length > 4 && (
                              <button
                                type="button"
                                className="rp-see-more-items-btn"
                                onClick={() => toggleOrderDetailsExpand(review.id)}
                              >
                                {expandedOrderDetails[review.id]
                                  ? 'See Less'
                                  : `+${review.metadata.order_summary.item_names.length - 4} See More`}
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Card Footer */}
                  <div className="rp-card-footer">
                    <span className="rp-timestamp">{formatDate(review.created_at)}</span>

                    <div className="rp-footer-actions">


                      {/* Action Resolution Button */}
                      {review.ticket_status === 'OPEN' && (
                        <button
                          className="rp-action-resolve-btn"
                          onClick={() => {
                            setResolvingReview(review);
                            setManagerExplanation(review.manager_explanation || '');
                            setIssueCategory(review.issue_category || '');
                            setPublicReplyText(review.public_reply_text || '');
                            setReplyToUserWhatsapp(false);
                            setResolveError(null);
                          }}
                        >
                          <FontAwesomeIcon icon={faStar} />
                          <span>Resolve Ticket</span>
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Resolution Notes Box if Closed */}
                  {review.ticket_status === 'CLOSED' && (review.issue_category || review.manager_explanation) && (
                    <div className="rp-resolution-details-box">
                      <div className="rp-resolution-header">
                        <span className="rp-resolution-title">
                          <FontAwesomeIcon icon={faCheckCircle} className="rp-check-icon" />
                          Resolved Ticket Summary
                        </span>
                        {review.resolved_at && (
                          <span className="rp-resolution-date">
                            Closed on {formatDate(review.resolved_at)}
                          </span>
                        )}
                      </div>
                      <div className="rp-resolution-body">
                        {review.issue_category && (
                          <p>
                            <strong>Category:</strong>
                            <span className="rp-category-badge">{review.issue_category.replace('_', ' ')}</span>
                          </p>
                        )}
                        {review.manager_explanation && (
                          <p>
                            <strong>Internal Resolution Action:</strong> <br />
                            <span className="rp-explanation-text">{review.manager_explanation}</span>
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Public Response Sent Section (on ALL reviews with a public reply, ticket or non-ticket) */}
                  {review.public_reply_text && (
                    <div className="rp-public-reply-section" style={{ marginTop: '12px' }}>
                      <div className="rp-reply-header-toggle" onClick={() => toggleReplyExpand(review.id)}>
                        <span className="rp-reply-label">
                          <FontAwesomeIcon icon={faCommentDots} />
                          Public Response Sent
                        </span>
                        <button type="button" className="rp-toggle-reply-btn">
                          <FontAwesomeIcon icon={expandedReplies[review.id] ? faChevronUp : faChevronDown} />
                          <span>{expandedReplies[review.id] ? 'Hide Reply' : 'View Reply'}</span>
                        </button>
                      </div>

                      {expandedReplies[review.id] && (
                        <div className="rp-reply-expanded-body">
                          <blockquote className="rp-public-reply-blockquote">
                            "{review.public_reply_text}"
                          </blockquote>

                          {/* Sync Status alerts */}
                          {review.public_reply_status === 'FAILED' && (
                            <div className="rp-sync-alert rp-sync-alert-failed">
                              <FontAwesomeIcon icon={faExclamationTriangle} />
                              <span>External Sync Failed: {review.public_reply_error || 'Expired API Token'}</span>
                            </div>
                          )}
                          {review.public_reply_status === 'PENDING' && (
                            <div className="rp-sync-alert rp-sync-alert-pending">
                              <FontAwesomeIcon icon={faSpinner} spin />
                              <span>Sync Pending to Google/Zomato...</span>
                            </div>
                          )}
                          {review.public_reply_status === 'POSTED' && (
                            <div className="rp-sync-alert rp-sync-alert-posted">
                              <FontAwesomeIcon icon={faCheckCircle} />
                              <span>Successfully published to Google/Zomato</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination controls */}
        {!loading && reviews.length > 0 && pagination.total_pages > 1 && (
          <div className="rp-pagination-wrapper">
            <span className="rp-pagination-info">
              Showing <strong>{((page - 1) * limit) + 1}</strong> - <strong>{Math.min(page * limit, pagination.total_records)}</strong> of <strong>{pagination.total_records}</strong> reviews
            </span>
            <div className="rp-pagination-controls">
              <button
                className="rp-page-arrow-btn"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>

              <div className="rp-page-indicators">
                {[...Array(pagination.total_pages)].map((_, i) => {
                  const pageNum = i + 1;
                  // Display only a small cohort of pages around active page to keep navigation clean
                  if (
                    pageNum === 1 ||
                    pageNum === pagination.total_pages ||
                    Math.abs(pageNum - page) <= 1
                  ) {
                    return (
                      <button
                        key={pageNum}
                        className={`rp-page-number-btn ${page === pageNum ? 'rp-page-active' : ''}`}
                        onClick={() => setPage(pageNum)}
                      >
                        {pageNum}
                      </button>
                    );
                  } else if (
                    (pageNum === 2 && page > 3) ||
                    (pageNum === pagination.total_pages - 1 && page < pagination.total_pages - 2)
                  ) {
                    return <span key={pageNum} className="rp-page-ellipsis">...</span>;
                  }
                  return null;
                })}
              </div>

              <button
                className="rp-page-arrow-btn"
                onClick={() => setPage(p => Math.min(pagination.total_pages, p + 1))}
                disabled={page === pagination.total_pages}
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Resolution Overlay Dialog Modal */}
      {resolvingReview && (
        <div className="rp-modal-overlay">
          <div className="rp-modal">
            <div className="rp-modal-header">
              <h3>Resolve Review Ticket #{resolvingReview.id}</h3>
              <button className="rp-modal-close-btn" onClick={() => setResolvingReview(null)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <form onSubmit={handleResolveSubmit} className="rp-modal-form">
              {resolveError && (
                <div className="rp-modal-error">
                  <FontAwesomeIcon icon={faExclamationTriangle} />
                  <span>{resolveError}</span>
                </div>
              )}

              {/* Display original review text snippet */}
              <div className="rp-modal-original-feedback">
                <span className="rp-modal-section-label">Original Feedback:</span>
                <p>"{resolvingReview.text || 'No comments text provided'}"</p>
                <div className="rp-modal-review-stats">
                  <span>Rating: {resolvingReview.rating}</span>
                  <span>Contact: {resolvingReview.reviewer_contact || 'None'}</span>
                </div>
              </div>

              {/* Issue Category Choice Chips */}
              <div className="rp-modal-field">
                <label className="rp-modal-field-label">Issue Category</label>
                <div className="rp-category-chips">
                  {[
                    { label: 'Food Quality', value: 'FOOD_QUALITY' },
                    { label: 'Service', value: 'SERVICE' },
                    { label: 'Ambience', value: 'AMBIENCE' },
                    { label: 'Hygiene', value: 'HYGIENE' }
                  ].map(cat => (
                    <button
                      key={cat.value}
                      type="button"
                      className={`rp-chip-btn ${issueCategory === cat.value ? 'rp-chip-active' : ''}`}
                      onClick={() => setIssueCategory(issueCategory === cat.value ? '' : cat.value)}
                    >
                      {cat.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Manager Explanation textarea */}
              <div className="rp-modal-field">
                <label htmlFor="explanation" className="rp-modal-field-label rp-required">
                  Internal Manager Resolution Actions
                </label>
                <textarea
                  id="explanation"
                  className="rp-modal-textarea"
                  placeholder="Detail actions taken (e.g. called guest, processed refund, offered dessert on next visit)..."
                  rows="3"
                  value={managerExplanation}
                  onChange={(e) => setManagerExplanation(e.target.value)}
                  required
                />
              </div>

              {/* Public Reply text (Optional for Google / Zomato) */}
              {(resolvingReview.source === 'GOOGLE' || resolvingReview.source === 'ZOMATO') && (
                <div className="rp-modal-field">
                  <label htmlFor="public-reply" className="rp-modal-field-label">
                    {resolvingReview.public_reply_text
                      ? `Edit Public Reply on ${resolvingReview.source === 'GOOGLE' ? 'Google Business' : 'Zomato'}`
                      : `Public Reply on ${resolvingReview.source === 'GOOGLE' ? 'Google Business' : 'Zomato'}`
                    }
                  </label>
                  <textarea
                    id="public-reply"
                    className="rp-modal-textarea"
                    placeholder={
                      resolvingReview.public_reply_text
                        ? "Edit published reply..."
                        : "Provide apology or explanation to show publicly on the listing..."
                    }
                    rows="3"
                    value={publicReplyText}
                    onChange={(e) => setPublicReplyText(e.target.value)}
                  />
                  <span className="rp-field-help">
                    {resolvingReview.public_reply_text
                      ? "An automatic public reply was already published. Editing this text will update the published response."
                      : "No public reply has been sent yet. Entering text here will publish a new response."
                    }
                  </span>
                </div>
              )}

              {/* WhatsApp Reply to Customer Checkbox (Visible for reviews with phone number) */}
              {resolvingReview.reviewer_contact && (
                <div className="rp-modal-field" style={{ marginTop: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      id="reply-to-user-whatsapp-checkbox"
                      checked={replyToUserWhatsapp}
                      onChange={(e) => setReplyToUserWhatsapp(e.target.checked)}
                      style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                    />
                    <label htmlFor="reply-to-user-whatsapp-checkbox" className="rp-modal-field-label" style={{ margin: 0, cursor: 'pointer', fontWeight: 600 }}>
                      {resolvingReview.source === 'GOOGLE'
                        ? 'Send WhatsApp to customer to update review'
                        : 'Send WhatsApp to customer for winback feedback'}
                    </label>
                  </div>
                  <span className="rp-field-help" style={{ marginLeft: '24px', marginTop: '4px', display: 'block' }}>
                    Will send a WhatsApp to {resolvingReview.reviewer_contact}.
                  </span>
                </div>
              )}

              {/* Action Buttons */}
              <div className="rp-modal-actions">
                <button
                  type="button"
                  className="rp-btn-secondary"
                  onClick={() => setResolvingReview(null)}
                  disabled={submittingResolve}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rp-btn-primary"
                  disabled={submittingResolve}
                >
                  {submittingResolve ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin />
                      <span>Resolving...</span>
                    </>
                  ) : (
                    <span>Submit Resolution</span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Customer History Modal */}
      {showHistoryModal && (
        <CustomerHistoryModal
          customerId={selectedCustomerId}
          token={activeToken}
          onClose={() => setShowHistoryModal(false)}
        />
      )}
    </div>
  );
};

export default ReviewsPage;
